import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastTypes, GlobalToastTypeNames } from 'types';

import {
  useLinkPandaDocDocumentMutation,
  useUnlinkPandaDocDocumentMutation,
} from 'core/graphql';

import { useToasts } from 'core/hooks';

interface IPandadocLinkHook {
  /** Attach a `candidate` document to an existing agreement, then refetch. */
  link: (pandadocId: string, agreementId: string) => Promise<void>;
  /** Detach a manually `linked` document from its agreement, then refetch. */
  unlink: (pandadocId: string) => Promise<void>;
  /** True while either mutation is in flight. */
  loading: boolean;
}

/**
 * Reads the backend `errors` JSONString envelope (null on success, otherwise an
 * object like `{"agreement_id": "…"}`); returns the first message when present.
 */
const firstError = (errors: unknown): string | null => {
  if (!errors) {
    return null;
  }
  if (typeof errors === 'string') {
    return errors;
  }
  if (typeof errors === 'object') {
    const values = Object.values(errors as Record<string, unknown>);
    const message = values.find((value) => Boolean(value));
    if (message != null) {
      return String(message);
    }
  }
  return String(errors);
};

/**
 * Wraps the link / unlink PandaDoc-document mutations with success/error toasts
 * and an `onDone` refetch callback. The candidate/linked affordances on the
 * student card call these; the tab re-queries its document connection on `onDone`
 * so the freshly (un)linked row re-renders with its new `matchType`.
 */
export const usePandadocLink = (onDone?: () => void): IPandadocLinkHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [
    linkMutation,
    { loading: linking },
  ] = useLinkPandaDocDocumentMutation();
  const [
    unlinkMutation,
    { loading: unlinking },
  ] = useUnlinkPandaDocDocumentMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.PandadocSync,
      });
    },
    [addToast],
  );

  const link = useCallback(
    async (pandadocId: string, agreementId: string): Promise<void> => {
      try {
        const result = await linkMutation({
          variables: { pandadocId, agreementId },
        });
        const message = firstError(result.data?.linkPandadocDocument?.errors);
        if (message) {
          notify(
            ToastTypes.error,
            t('pages.studentDetail.pandadoc.linkError', { error: message }),
          );
          return;
        }
        notify(
          ToastTypes.success,
          t('pages.studentDetail.pandadoc.linkSuccess'),
        );
        if (onDone) onDone();
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.studentDetail.pandadoc.linkError', {
            error: (error as Error).message,
          }),
        );
      }
    },
    [linkMutation, notify, t, onDone],
  );

  const unlink = useCallback(
    async (pandadocId: string): Promise<void> => {
      try {
        const result = await unlinkMutation({ variables: { pandadocId } });
        const message = firstError(result.data?.unlinkPandadocDocument?.errors);
        if (message) {
          notify(
            ToastTypes.error,
            t('pages.studentDetail.pandadoc.linkError', { error: message }),
          );
          return;
        }
        notify(
          ToastTypes.success,
          t('pages.studentDetail.pandadoc.unlinkSuccess'),
        );
        if (onDone) onDone();
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.studentDetail.pandadoc.linkError', {
            error: (error as Error).message,
          }),
        );
      }
    },
    [unlinkMutation, notify, t, onDone],
  );

  return { link, unlink, loading: linking || unlinking };
};
